import type React from "react";
import type { AriaAttributes, FocusEventHandler } from "react";

import { UploadDocumentIcon } from "@/components/CustomIcons/UploadDocumentIcon";
import { UploadImageIcon } from "@/components/CustomIcons/UploadImageIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { PlusCircleIcon } from "@/components/Icons/PlusCircleIcon";
import { buttonVariants } from "@/design-system/Button";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";

import { ClapperboardIcon } from "../Icons/ClapperboardIcon";
import { MediaXButton } from "./MediaXButton";

export interface MediaBaseProps extends AriaAttributes {
  texts?: {
    add?: string;
    edit?: string;
  };
  onAdd?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  dropActive?: boolean;
  "data-testid"?: string;
  "data-add-testid"?: string;
  "data-edit-testid"?: string;
  "data-delete-testid"?: string;
  onFocus?: FocusEventHandler;
  onBlur?: FocusEventHandler;
  disabled?: boolean;
  children?: React.ReactNode;
}
type MediaProps = {
  type?: "image" | "document" | "video";
};
export function Media(props: MediaBaseProps & MediaProps): React.ReactNode {
  const { t } = useTranslation();

  const innerButtonStyles =
    "disabled:text-text-disabled rounded-lg focus-visible:outline-hidden focus-visible:before:content-[''] focus-visible:before:absolute focus-visible:before:-inset-0.5 focus-visible:before:pointer-events-none focus-visible:before:ring-1 focus-visible:before:ring-border-tertiary focus-visible:before:rounded-lg";

  if (!props.children) {
    return (
      <MediaInputBox {...props}>
        <button
          className={twResolve(
            buttonVariants({ variant: "subtle", size: "sm", fullWidth: true, type: "button" }),
            "size-full! gap-space-sm px-space-sm text-text-secondary",
            innerButtonStyles,
            props.disabled && "cursor-not-allowed",
          )}
          data-testid={props["data-add-testid"]}
          type="button"
          onClick={props.onAdd}
          onFocus={props.onFocus}
          onBlur={props.onBlur}
          disabled={props.disabled}
        >
          {props.type === "image" && <UploadImageIcon />}
          {props.type === "document" && <UploadDocumentIcon />}
          {props.type === "video" && <ClapperboardIcon />}
          {!props.type && <PlusCircleIcon />}
          <span className="max-w-media-action-label whitespace-pre-line">
            {props.texts?.add || t("common.action.add")}
          </span>
        </button>
      </MediaInputBox>
    );
  }

  return (
    <MediaInputBox {...props}>
      {props.onEdit != null && !props.disabled && (
        <button
          className={twResolve(
            buttonVariants({ variant: "subtle", size: "sm", fullWidth: true, type: "button" }),
            "absolute inset-0 z-raised size-full! max-w-none flex-1 gap-space-xs bg-background-default-inverse/60 text-text-default-inverse opacity-0 hover:bg-background-default-inverse/60 focus-visible:bg-background-default-inverse/60 pressed:bg-background-default-inverse/60 hocus:opacity-100",
            innerButtonStyles,
          )}
          data-testid={props["data-edit-testid"]}
          onClick={props.onEdit}
          type="button"
        >
          <Edit05Icon />
          {props.texts?.edit || t("common.action.edit")}
        </button>
      )}
      {props.onDelete != null && !props.disabled && (
        <div className="absolute top-0 right-0 z-10 translate-x-1/2 -translate-y-1/2">
          <MediaXButton
            data-testid={props["data-delete-testid"]}
            onClick={props.onDelete}
            title={t("common.action.delete")}
          />
        </div>
      )}
      {props.children}
    </MediaInputBox>
  );
}
function MediaInputBox(
  props: MediaBaseProps & {
    children: React.ReactNode;
  },
) {
  return (
    <div
      data-testid={props["data-testid"]}
      className={twResolve(
        "relative aspect-video h-media-input min-w-media-input-min rounded-lg border border-border-secondary transition-colors hocus:bg-background-secondary",
        props["aria-invalid"]
          ? "border-border-danger! focus-within:ring-4 focus-within:ring-border-danger-tertiary"
          : undefined,
        props.disabled && "bg-background-secondary",
        props.dropActive && "border-border-brand",
      )}
      aria-invalid={props["aria-invalid"]}
    >
      {props.children}
    </div>
  );
}
